import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError, money } from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Brain, ChartLineUp, CurrencyDollar, Plus, Broadcast, Info, ArrowRight,
} from "@phosphor-icons/react";

function PlanMiniTable({ plans, highlightTotal, testid }) {
  if (!plans?.length) {
    return (
      <p className="text-sm text-muted-foreground" data-testid={`${testid}-empty`}>
        No plans published yet. Ask your admin to add pricing plans.
      </p>
    );
  }
  return (
    <div className="overflow-x-auto rounded-lg border border-border" data-testid={testid}>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-muted/40 text-left text-[11px] uppercase tracking-wide text-muted-foreground">
            <th className="px-3 py-2 font-medium">Plan</th>
            <th className="px-3 py-2 font-medium">Channels</th>
            <th className="px-3 py-2 font-medium text-right">Price / ch</th>
          </tr>
        </thead>
        <tbody>
          {plans.map((p) => {
            const active = highlightTotal != null
              && highlightTotal >= p.channels_min
              && highlightTotal <= p.channels_max;
            return (
              <tr
                key={p.id}
                className={`border-b border-border/60 last:border-0 ${
                  active ? "bg-primary/10" : ""
                }`}
              >
                <td className="px-3 py-2 font-medium">
                  {p.name}
                  {active && (
                    <span className="ml-2 text-[10px] uppercase tracking-wide text-primary font-semibold">
                      Applies
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 tabular-nums text-muted-foreground">
                  {p.channels_min} – {p.channels_max}
                </td>
                <td className="px-3 py-2 tabular-nums font-semibold text-right">
                  {money(p.price_minor)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Row({ label, value, testid, strong, danger }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span
        data-testid={testid}
        className={`tabular-nums text-right ${strong ? "font-semibold" : ""} ${
          danger ? "text-destructive" : ""
        }`}
      >
        {value}
      </span>
    </div>
  );
}

function LimitBuyPanel({
  kind,
  title,
  subtitle,
  icon: Icon,
  accent,
  currentLimit,
  plans,
  balance,
  channels,
  setChannels,
  quote,
  quoting,
  onBuy,
}) {
  const base = currentLimit != null && currentLimit > 0 ? currentLimit : 0;
  const n = parseInt(channels, 10);
  const previewTotal = Number.isFinite(n) && n > 0 ? base + n : null;
  const noPlan = quote?.blocked_reason === "no_plan";
  const canConfirm = quote && quote.charge_minor != null && quote.sufficient && !noPlan;

  return (
    <section
      className={`rounded-2xl border bg-card card-shadow overflow-hidden flex flex-col ${accent.border}`}
      data-testid={`call-limit-panel-${kind}`}
    >
      <div className={`px-5 py-4 border-b ${accent.header}`}>
        <div className="flex items-start gap-3">
          <div className={`w-12 h-12 rounded-xl grid place-items-center shrink-0 ${accent.iconBox}`}>
            <Icon className="w-6 h-6" weight="duotone" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-semibold tracking-tight">{title}</h2>
            <p className="text-sm text-muted-foreground mt-0.5">{subtitle}</p>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              Current limit
            </p>
            <p
              className="mt-1 text-4xl font-semibold tabular-nums tracking-tight"
              data-testid={`call-limit-current-${kind}`}
            >
              {base}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Concurrent channels right now
            </p>
          </div>
          {previewTotal != null && (
            <div className="text-right">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                After purchase
              </p>
              <p
                className={`mt-1 text-2xl font-semibold tabular-nums ${accent.total}`}
                data-testid={`call-limit-preview-total-${kind}`}
              >
                {previewTotal}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {base} + {n} channels
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="p-5 space-y-4 flex-1 flex flex-col">
        <div>
          <p className="text-sm font-medium mb-1.5">Published plans</p>
          <p className="text-xs text-muted-foreground mb-2">
            Price is taken from the plan that covers your <b>new total</b> channels
            (current + buying).
          </p>
          <PlanMiniTable
            plans={plans}
            highlightTotal={previewTotal}
            testid={`call-limit-plans-${kind}`}
          />
        </div>

        <div className="rounded-xl border border-border bg-muted/25 p-4 space-y-3 mt-auto">
          <div className="space-y-1.5 max-w-[200px]">
            <Label htmlFor={`buy-${kind}`}>Channels to buy</Label>
            <Input
              id={`buy-${kind}`}
              data-testid={`purchase-channels-${kind}`}
              type="number"
              min="1"
              step="1"
              value={channels}
              onChange={(e) => setChannels(e.target.value)}
              placeholder="e.g. 5"
            />
          </div>

          {quoting && (
            <p className="text-xs text-muted-foreground">Calculating price…</p>
          )}

          {noPlan && Number.isFinite(n) && n > 0 && (
            <p className="text-sm text-destructive" data-testid={`purchase-no-plan-${kind}`}>
              No plan covers {base + n} total channels. Ask your admin to add a matching plan.
            </p>
          )}

          {quote && quote.charge_minor != null && !noPlan && (
            <div
              className="rounded-lg border border-border bg-background p-3 space-y-1.5 text-sm"
              data-testid={`purchase-summary-${kind}`}
            >
              <Row
                label="Matching plan"
                value={quote.plan?.name || "—"}
                testid={`purchase-plan-${kind}`}
              />
              <Row
                label="New total channels"
                value={`${quote.current_limit} → ${quote.new_limit}`}
                testid={`purchase-new-limit-${kind}`}
              />
              <Row
                label="Price per channel"
                value={money(quote.price_per_channel_minor ?? quote.call_limit_price_minor)}
              />
              <Row
                label={`Prorated (${quote.remaining_days}/${quote.days_in_month} days left)`}
                value={money(quote.charge_minor)}
                testid={`purchase-charge-${kind}`}
                strong
              />
              <div className="border-t border-border my-1" />
              <Row label="Current balance" value={money(balance)} />
              <Row
                label="Balance after"
                value={money(quote.balance_after_minor)}
                testid={`purchase-balance-after-${kind}`}
                strong
                danger={!quote.sufficient}
              />
              {!quote.sufficient && (
                <p className="text-destructive text-xs pt-1" data-testid={`purchase-insufficient-${kind}`}>
                  Insufficient balance —{" "}
                  <Link to="/billing" className="underline">add cash</Link> first.
                </p>
              )}
            </div>
          )}

          <Button
            data-testid={`purchase-btn-${kind}`}
            className={`rounded-lg w-full sm:w-auto ${accent.button}`}
            disabled={quoting || !canConfirm}
            onClick={onBuy}
          >
            <Plus className="w-4 h-4 mr-1.5" weight="bold" />
            Buy {title.split(" ")[0]} channels
          </Button>
        </div>
      </div>
    </section>
  );
}

export default function CallLimit() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiChannels, setAiChannels] = useState("");
  const [pdChannels, setPdChannels] = useState("");
  const [aiQuote, setAiQuote] = useState(null);
  const [pdQuote, setPdQuote] = useState(null);
  const [quotingAi, setQuotingAi] = useState(false);
  const [quotingPd, setQuotingPd] = useState(false);
  const [confirm, setConfirm] = useState(null); // { kind, quote }
  const [buying, setBuying] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/billing/call-limit");
      setData(res.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const n = parseInt(aiChannels, 10);
    if (!n || n <= 0) { setAiQuote(null); return undefined; }
    let cancelled = false;
    setQuotingAi(true);
    const timer = setTimeout(async () => {
      try {
        const { data: q } = await api.get("/billing/call-limit/quote", {
          params: { channels: n, kind: "ai" },
        });
        if (!cancelled) setAiQuote(q);
      } catch (e) {
        if (!cancelled) {
          setAiQuote(null);
          toast.error(formatApiError(e.response?.data?.detail, e));
        }
      } finally {
        if (!cancelled) setQuotingAi(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [aiChannels]);

  useEffect(() => {
    const n = parseInt(pdChannels, 10);
    if (!n || n <= 0) { setPdQuote(null); return undefined; }
    let cancelled = false;
    setQuotingPd(true);
    const timer = setTimeout(async () => {
      try {
        const { data: q } = await api.get("/billing/call-limit/quote", {
          params: { channels: n, kind: "pd" },
        });
        if (!cancelled) setPdQuote(q);
      } catch (e) {
        if (!cancelled) {
          setPdQuote(null);
          toast.error(formatApiError(e.response?.data?.detail, e));
        }
      } finally {
        if (!cancelled) setQuotingPd(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [pdChannels]);

  const purchase = async () => {
    if (!confirm?.quote) return;
    const kind = confirm.kind;
    const n = confirm.quote.channels;
    setBuying(true);
    try {
      const { data: res } = await api.post("/billing/call-limit/purchase", {
        channels: n,
        kind,
      });
      const label = kind === "ai" ? "AI" : "PD";
      toast.success(
        `Added ${res.channels_added} ${label} channel(s) — new limit ${res.new_limit}. Charged ${money(res.charge_minor)}.`,
      );
      setConfirm(null);
      if (kind === "ai") { setAiChannels(""); setAiQuote(null); }
      else { setPdChannels(""); setPdQuote(null); }
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setBuying(false);
    }
  };

  const balance = data?.cash_balance_minor || 0;
  const aiLimit = data?.ai_call_limit ?? data?.concurrent_call_limit;
  const pdLimit = data?.pd_call_limit;

  return (
    <div className="space-y-6 max-w-6xl" data-testid="call-limit-page">
      <PageHeader
        title="Call limits"
        subtitle="Buy concurrent AI or Predictive (PD) channels. Price comes from the admin plan that matches your new total."
      />

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="rounded-xl border border-border bg-card card-shadow p-4" data-testid="cl-balance">
              <div className="flex items-center gap-2 text-muted-foreground">
                <CurrencyDollar className="w-4 h-4 text-primary" weight="duotone" />
                <span className="text-[11px] font-semibold uppercase tracking-[0.12em]">Cash balance</span>
              </div>
              <p className="mt-2 text-2xl font-semibold tabular-nums">{money(balance)}</p>
              <Link
                to="/billing"
                className="mt-2 inline-flex items-center gap-1 text-xs text-primary hover:underline"
              >
                Add balance <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
            <div className="rounded-xl border border-border bg-card card-shadow p-4" data-testid="cl-active">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Broadcast className="w-4 h-4 text-emerald-600" weight="duotone" />
                <span className="text-[11px] font-semibold uppercase tracking-[0.12em]">Active calls now</span>
              </div>
              <p className="mt-2 text-2xl font-semibold tabular-nums">{data?.active_calls ?? 0}</p>
              <p className="mt-1 text-xs text-muted-foreground">Using your capacity right now</p>
            </div>
            <div className="rounded-xl border border-border bg-card card-shadow p-4">
              <div className="flex items-start gap-2 text-sm text-muted-foreground">
                <Info className="w-4 h-4 text-primary mt-0.5 shrink-0" weight="fill" />
                <p>
                  <b className="text-foreground">How price works:</b>{" "}
                  current limit + channels you buy = total. We charge the per-channel
                  price from the plan that covers that total, prorated for days left this month.
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <LimitBuyPanel
              kind="ai"
              title="AI call limit"
              subtitle="Concurrent capacity for VICI Bot and AI Agent campaigns."
              icon={Brain}
              accent={{
                border: "border-violet-200/80 dark:border-violet-900/50",
                header: "bg-violet-50/80 dark:bg-violet-950/30 border-violet-200/60 dark:border-violet-900/40",
                iconBox: "bg-violet-600/15 text-violet-700 dark:text-violet-300",
                total: "text-violet-700 dark:text-violet-300",
                button: "",
              }}
              currentLimit={aiLimit}
              plans={data?.ai_plans || []}
              balance={balance}
              channels={aiChannels}
              setChannels={setAiChannels}
              quote={aiQuote}
              quoting={quotingAi}
              onBuy={() => setConfirm({ kind: "ai", quote: aiQuote })}
            />
            <LimitBuyPanel
              kind="pd"
              title="PD call limit"
              subtitle="Concurrent capacity for Predictive and Manual campaigns."
              icon={ChartLineUp}
              accent={{
                border: "border-emerald-200/80 dark:border-emerald-900/50",
                header: "bg-emerald-50/80 dark:bg-emerald-950/30 border-emerald-200/60 dark:border-emerald-900/40",
                iconBox: "bg-emerald-600/15 text-emerald-700 dark:text-emerald-300",
                total: "text-emerald-700 dark:text-emerald-300",
                button: "",
              }}
              currentLimit={pdLimit}
              plans={data?.pd_plans || []}
              balance={balance}
              channels={pdChannels}
              setChannels={setPdChannels}
              quote={pdQuote}
              quoting={quotingPd}
              onBuy={() => setConfirm({ kind: "pd", quote: pdQuote })}
            />
          </div>
        </>
      )}

      <AlertDialog open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}>
        <AlertDialogContent data-testid="purchase-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Confirm {confirm?.kind === "pd" ? "PD" : "AI"} channel purchase
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.quote && (
                <>
                  Add <b>{confirm.quote.channels}</b> channel(s)
                  ({confirm.quote.current_limit} → {confirm.quote.new_limit}),
                  using plan <b>{confirm.quote.plan?.name}</b> at{" "}
                  {money(confirm.quote.price_per_channel_minor)}/channel.
                  Charge now: <b>{money(confirm.quote.charge_minor)}</b>.
                  Balance after: {money(confirm.quote.balance_after_minor)}.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="purchase-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="purchase-confirm-btn"
              disabled={buying}
              onClick={(e) => { e.preventDefault(); purchase(); }}
            >
              {buying ? "Processing…" : "Confirm purchase"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
